/**
 * useTrack — lightweight event tracker
 * Logs user actions to Firestore 'activityLog' collection.
 * Captures: event name, page, userId, county (if known), device, timestamp.
 * Privacy: no PII beyond userId. Location is county-level from user profile,
 * never precise GPS. Can be disabled by user (add opt-out flag to user doc).
 */
import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { addDoc, collection } from 'firebase/firestore';

type TrackEvent =
  | 'page_view'
  | 'product_viewed'
  | 'product_added_to_cart'
  | 'checkout_started'
  | 'order_placed'
  | 'sponsor_configurator_used'
  | 'game_played'
  | 'game_completed'
  | 'restoration_track_viewed'
  | 'gallery_viewed'
  | 'whatsapp_cta_clicked'
  | 'bulk_quote_requested'
  | 'sponsor_enquiry_sent'
  | 'wallet_topup_initiated'
  | 'phone_verified'
  | 'ai_chat_opened'
  | 'ai_chat_message'
  | 'wizard_started'
  | 'wizard_completed';

interface TrackPayload {
  event: TrackEvent;
  page?: string;
  productName?: string;
  productCategory?: string;
  amount?: number;
  county?: string;
  extra?: Record<string, any>;
}

export function useTrack() {
  const { user } = useAuth();

  const track = useCallback(async (payload: TrackPayload) => {
    try {
      const device = /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
      await addDoc(collection(db, 'activityLog'), {
        ...payload,
        userId:    user?.id ?? null,
        county:    user ? (payload.county ?? null) : null,
        device,
        referrer:  document.referrer || null,
        page:      payload.page ?? window.location.pathname,
        timestamp: new Date().toISOString(),
        // session: could add sessionStorage-based session ID here
      });
    } catch { /* never block UI on tracking failure */ }
  }, [user]);

  return { track };
}
